/**
 * 🔐 Auth0 Callback Handler for Coach Cedar
 * Handles OAuth redirects and integrates with Coach Cedar backend
 * Cross-platform support for web and native
 */

import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react'; // Web-only import (App.tsx uses Auth0Provider)
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { storage } from '../services/storage';
import { secureTokenStorage } from '../services/secureTokenStorage';
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL
import { Platform } from 'react-native'; // Mission 20: Platform detection for web vs native
import { readAndClearPendingAttribution } from '../utils/attributionCapture';

interface CallbackState {
  status: 'processing' | 'success' | 'error';
  message: string;
  error?: string;
}

export const Auth0Callback: React.FC = () => {
  // Minimal logging for production
  console.log('🔐 Auth0 Callback: Processing authentication...');
  
  const { user: auth0User, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>({
    status: 'processing',
    message: 'Processing authentication...'
  });

  // StrictMode-compatible execution control
  const isExecutingRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const processedUrlRef = useRef<string | null>(null);
  
  // Add a flag to track if we've started processing
  const hasStartedProcessing = useRef(false);

  useEffect(() => {
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.error('🚨 AUTHENTICATION TIMEOUT: Process took longer than 15 seconds');
      console.error('🚨 Auth0 State at timeout:', { isLoading, isAuthenticated, hasUser: !!auth0User });
      setState({
        status: 'error',
        message: 'Authentication timeout',
        error: 'Authentication is taking too long. Please try again or clear your browser data.'
      });
    }, 15000); // 15 second timeout for entire process

    // Add a shorter timeout specifically for Auth0 loading
    const auth0TimeoutId = setTimeout(() => {
      if (isLoading) {
        console.error('🚨 AUTH0 LOADING TIMEOUT: Auth0 stuck in loading state for 8 seconds');
        console.error('🚨 Forcing Auth0 state check...');
        // Try to force a state update by calling the handler
        handleAuth0Callback();
      }
    }, 8000); // 8 second timeout for Auth0 loading specifically

    const handleAuth0Callback = async () => {
      // 🚨 MISSION 20: Platform guard for window access on Android/iOS
      const currentUrl = Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.href
        : '';

      // CRITICAL: Only process callback on /auth/callback route to prevent infinite loops
      if (Platform.OS === 'web' && typeof window !== 'undefined' && !window.location.pathname.includes('/auth/callback')) {
        console.log('🚫 Not on callback route, skipping processing');
        return;
      }

      // If we've already completed processing, don't do it again
      if (hasCompletedRef.current) {
        console.log('✅ Auth0Callback already completed, skipping...');
        return;
      }

      // If we're currently executing, don't start again
      if (isExecutingRef.current) {
        console.log('🔄 Auth0Callback execution already in progress, skipping...');
        return;
      }
      
      // Wait for Auth0 to finish loading
      if (isLoading) {
        console.log('⏳ Auth0 still loading, waiting for completion...');
        // Set a flag that we've seen the callback and are waiting
        hasStartedProcessing.current = true;
        return;
      }

      // If we've already processed this specific URL, skip
      if (processedUrlRef.current === currentUrl) {
        console.log('🔄 Auth0Callback already processed this specific URL, skipping...');
        return;
      }

      // Mark this URL as being processed (StrictMode-safe)
      isExecutingRef.current = true;
      processedUrlRef.current = currentUrl;
      console.log('🔐 Processing Auth0 authentication for:', currentUrl);

      // Check for Auth0 error in URL parameters (e.g., connection not enabled)
      const urlParams = new URLSearchParams(window.location.search);

      // Check if this is a token-based URL (not OAuth) - do this before Auth0 error checking
      const tokenParam = urlParams.get('token');
      if (tokenParam && tokenParam.startsWith('invite_')) {
        console.log('🔧 Token-based authentication detected, redirecting to Splash');
        // 🚨 MISSION 20: Platform guard for window redirect
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.location.href = `/?token=${tokenParam}`;
        } else {
          navigate('/', { replace: true });
        }
        return;
      }

      const auth0Error = urlParams.get('error');
      const auth0ErrorDescription = urlParams.get('error_description');
      
      if (auth0Error) {
        console.error('🚨 Auth0 Error:', auth0Error);
        console.error('🚨 Auth0 Error Description:', auth0ErrorDescription);
        
        let userFriendlyMessage = 'Authentication failed. Please try again.';
        
        if (auth0Error === 'invalid_request' && auth0ErrorDescription?.includes('connection is not enabled')) {
          userFriendlyMessage = 'Email registration is temporarily unavailable. Please try signing in with Google or contact support.';
          console.error('🚨 ADMIN ACTION REQUIRED: Auth0 Database Connection not enabled in dashboard');
        } else if (auth0Error === 'invalid_request' && auth0ErrorDescription?.includes('user already exists')) {
          userFriendlyMessage = 'This email is already registered. Please sign in instead, or use a different email address.';
        } else if (auth0Error === 'signup_error' || auth0ErrorDescription?.includes('already exists')) {
          userFriendlyMessage = 'This email is already registered. Please sign in instead, or try with a different email address.';
        }
        
        setState({
          status: 'error',
          message: 'Authentication Error',
          error: userFriendlyMessage
        });
        
        // Redirect back to login after showing error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 5000);
        
        return;
      }
      
      try {
        // Wait for Auth0 to finish processing
        if (isLoading) {
          console.log('⏳ Auth0 still loading...');
          return;
        }

        console.log('🔍 Auth0 state check:', { isAuthenticated, hasUser: !!auth0User });

        if (!isAuthenticated || !auth0User) {
          console.error('❌ Auth0 authentication failed:', { isAuthenticated, hasUser: !!auth0User });
          throw new Error('Authentication failed - no user data received from Auth0');
        }

        console.log('✅ Auth0 authentication successful for:', auth0User.email);

        setState({
          status: 'processing',
          message: 'Verifying with Coach Cedar...'
        });

        // Get Auth0 access token
        console.log('🔑 Getting Auth0 access token...');
        let accessToken;
        try {
          const tokenStartTime = Date.now();
          accessToken = await getAccessTokenSilently({
            timeoutInSeconds: 10, // Reduced timeout to identify hanging
            cacheMode: 'off' // Force fresh token to avoid cache issues
          });
          const tokenEndTime = Date.now();
          console.log(`✅ Got Auth0 token in ${tokenEndTime - tokenStartTime}ms`);
        } catch (tokenError) {
          console.error('❌ Failed to get Auth0 access token:', tokenError);
          
          // Handle specific Auth0 token errors
          if (tokenError instanceof Error) {
            if (tokenError.message.includes('Invalid state')) {
              throw new Error('Auth0 session expired. Please try signing in again.');
            } else if (tokenError.message.includes('consent_required')) {
              throw new Error('Additional consent required. Please try signing in again.');
            } else if (tokenError.message.includes('login_required')) {
              throw new Error('Login session expired. Please try signing in again.');
            }
          }
          
          // Generic token error
          throw new Error(`Authentication token error: ${tokenError instanceof Error ? tokenError.message : 'Unknown error'}`);
        }

        // Get device fingerprint for persistent login
        console.log('📱 Getting device fingerprint...');
        const { deviceFingerprinting } = await import('../services/deviceFingerprinting');
        const deviceInfo = deviceFingerprinting.getDeviceInfo();
        console.log('📱 Device fingerprint ready');
        
        console.log('🔄 Calling Coach Cedar backend...');

        // Platform Analytics — attach any UTM params captured at landing.
        // Never blocks signup if the read fails.
        let attribution: Record<string, unknown> | undefined;
        try {
          const pending = await readAndClearPendingAttribution();
          if (pending) {
            const { captured_at: _ignored, ...forward } = pending;
            attribution = forward;
          }
        } catch (attrErr) {
          console.warn('Auth0Callback: attribution read failed', attrErr);
        }

        let response;
        const apiStartTime = Date.now(); // Move outside try block for catch access
        try {
          response = await fetch(`${API_BASE_URL}/api/auth0/verify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              access_token: accessToken,
              auth0_user: {
                sub: auth0User.sub,
                email: auth0User.email,
                name: auth0User.name,
                given_name: auth0User.given_name,
                family_name: auth0User.family_name,
                picture: auth0User.picture,
                email_verified: auth0User.email_verified
              },
              device_info: deviceInfo,  // Include device fingerprinting for persistent login
              ...(attribution ? { attribution } : {}),
            }),
          });
        } catch (fetchError) {
          const apiEndTime = Date.now();
          console.error(`❌ Backend API call failed after ${apiEndTime - apiStartTime}ms:`, fetchError);
          throw new Error('Network error connecting to Coach Cedar. Please check your connection and try again.');
        }

        const apiEndTime = Date.now();
        console.log(`📡 Backend responded in ${apiEndTime - apiStartTime}ms with status:`, response.status);

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
            console.error('❌ Backend error response:', errorData);
          } catch (parseError) {
            console.error('❌ Could not parse backend error response');
            errorData = {};
          }
          
          // Handle specific backend errors
          if (response.status === 403 && errorData.detail) {
            // Check if this is a waitlist error
            if (errorData.detail.error === 'waitlist_required' || errorData.detail.error === 'waitlist_pending') {
              // Show friendly message and redirect to coachcedar.com
              setState({
                status: 'error',
                message: 'Waitlist Required',
                error: errorData.detail.message || 'You need to join the waitlist at CoachCedar.com to access the app.'
              });
              
              // Redirect to coachcedar.com after 3 seconds
              setTimeout(() => {
                window.location.href = 'https://coachcedar.com';
              }, 3000);
              return; // Exit the function here
            }
            // Other 403 errors
            throw new Error(JSON.stringify(errorData));
          } else if (response.status === 404) {
            throw new Error('Auth0 verification endpoint not found. Please contact support.');
          } else if (response.status >= 500) {
            throw new Error('Coach Cedar server error. Please try again in a few minutes.');
          } else {
            throw new Error(errorData.detail || `Backend verification failed: ${response.status}`);
          }
        }

        const data = await response.json();
        console.log('✅ Coach Cedar verification successful:', {
          success: data.success,
          isNewUser: data.isNewUser,
          needsOnboarding: data.needsOnboarding,
          userId: data.user?.id
        });

        if (!data.success) {
          throw new Error(data.detail || 'Backend verification failed');
        }

        // Store Coach Cedar JWT token under both keys for compatibility:
        // 'jwt_token' is used by legacy web code (AuthContext, etc.)
        // 'cedar_jwt' is used by secureTokenStorage (SubscriptionService, etc.)
        await storage.setItem('jwt_token', data.token);
        await secureTokenStorage.saveTokens(accessToken, undefined, data.token);
        
        // Update Coach Cedar auth context (skip device registration - already done by backend)
        await login(data.user, true); // skipDeviceRegistration = true

        setState({
          status: 'success',
          message: data.needsOnboarding ? 'Welcome to Coach Cedar!' : 'Welcome back!'
        });

        // Mark as completed to prevent re-execution
        hasCompletedRef.current = true;

        // HIGHEST PRIORITY routing — email verification wall.
        // Email/password signups who haven't yet clicked the verification
        // link in their inbox are routed to /email-verification BEFORE
        // any other routing. Apple/Google users return
        // needsEmailVerification=false so this path doesn't fire for them.
        if (data?.needsEmailVerification) {
          console.log('🚧 ROUTING: User needs email verification — navigating to wall');
          setTimeout(() => navigate('/email-verification', { replace: true }), 200);
          return;
        }

        // Check if user needs name collection first (hybrid Auth0 approach)
        setTimeout(async () => {
          try {
            // Check if name collection is needed
            // Pass auth_method so backend can identify Apple Sign In users
            // (Apple Guideline 4: don't re-ask for name/email that SIWA already provided)
            const authMethod = data.user?.authMethod || '';
            const nameCheckResponse = await fetch(`${API_BASE_URL}/api/users/needs-name-collection?auth_method=${encodeURIComponent(authMethod)}`, {
              headers: {
                'Authorization': `Bearer ${data.token}`
              }
            });

            if (nameCheckResponse.ok) {
              const nameCheckData = await nameCheckResponse.json();

              if (nameCheckData.needs_name_collection) {
                console.log('🎯 User needs name collection - navigating to name collection');

                // Get waitlist data for pre-population
                const waitlistResponse = await fetch(`${API_BASE_URL}/api/users/waitlist-data`, {
                  headers: {
                    'Authorization': `Bearer ${data.token}`
                  }
                });

                const waitlistData = waitlistResponse.ok ? await waitlistResponse.json() : { is_waitlist_user: false };

                // Apple Guideline 4: If SIWA already provided names, pass them
                // so the name collection screen can skip/pre-fill name fields.
                const appleProvidedNames = nameCheckData.apple_provided_names;
                const prefilledNames = appleProvidedNames ? {
                  first_name: nameCheckData.first_name,
                  last_name: nameCheckData.last_name,
                } : undefined;

                // Navigate to name collection with data
                navigate('/collect-name', {
                  replace: true,
                  state: {
                    email: auth0User.email,
                    isWaitlistUser: waitlistData.is_waitlist_user,
                    waitlistData: prefilledNames || (waitlistData.is_waitlist_user ? {
                      first_name: waitlistData.first_name,
                      last_name: waitlistData.last_name
                    } : undefined)
                  }
                });
                return;
              }
            }
            
            // If name collection not needed, proceed with normal flow
            // 🔍 DEBUG: Check needsOnboarding value from backend
            console.log('🔍 ROUTING DEBUG: needsOnboarding =', data.needsOnboarding);
            console.log('🔍 ROUTING DEBUG: isNewUser =', data.isNewUser);
            console.log('🔍 ROUTING DEBUG: user has chat history =', !data.needsOnboarding);
            
            if (data.needsOnboarding) {
              console.log('🎯 User needs onboarding - navigating to onboarding');
              navigate('/onboarding', { replace: true });
            } else {
              console.log('🎯 User completed onboarding - navigating to chat');
              navigate('/chat', { replace: true });
            }
            
          } catch (nameCheckError) {
            console.error('❌ Failed to check name collection status:', nameCheckError);
            // Fallback to normal flow on error
            if (data.needsOnboarding) {
              navigate('/onboarding', { replace: true });
            } else {
              navigate('/chat', { replace: true });
            }
          }
        }, 800); // Reduced from 2000ms to 800ms for smoother flow

      } catch (error) {
        console.error('❌ Auth0 callback error:', error);
        
        // Handle Auth0 session/state errors with recovery
        if (error instanceof Error && (
          error.message.includes('Invalid state') ||
          error.message.includes('session expired') ||
          error.message.includes('login_required') ||
          error.message.includes('consent_required')
        )) {
          setState({
            status: 'error',
            message: 'Authentication session expired',
            error: 'Please clear your browser data and try signing in again, or use an incognito window.'
          });

          // Clear Auth0 cache and redirect to fresh login
          setTimeout(async () => {
            // 🚨 MISSION 20: Platform guard for localStorage (web-only Auth0 SDK cache)
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              localStorage.removeItem('@@auth0spajs@@::agMKpwVd5TWnjqua1ELYgX3dbxscEfZI::@@user@@');
              localStorage.removeItem('@@auth0spajs@@::agMKpwVd5TWnjqua1ELYgX3dbxscEfZI::default::openid profile email');
            }
            await storage.removeItem('jwt_token');
            
            // Navigate to root to trigger fresh Auth0 login
            navigate('/', { replace: true });
          }, 3000);
          return;
        }
        
        setState({
          status: 'error',
          message: 'Authentication failed',
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        });

        // Redirect to login after error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 5000);
      } finally {
        isExecutingRef.current = false;
      }
    };

    // Log Auth0 state changes for debugging
    console.log('🔍 Auth0 state update:', { 
      isLoading, 
      isAuthenticated, 
      hasUser: !!auth0User,
      hasStartedProcessing: hasStartedProcessing.current,
      isExecuting: isExecutingRef.current,
      hasCompleted: hasCompletedRef.current
    });

    handleAuth0Callback();

    // Cleanup timeouts on unmount
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(auth0TimeoutId);
      isExecutingRef.current = false;
      // Don't reset processedUrlRef or hasCompletedRef - they should persist
    };
    }, [isLoading, isAuthenticated, auth0User, getAccessTokenSilently]); // Include getAccessTokenSilently to ensure proper Auth0 state

  const getIcon = () => {
    switch (state.status) {
      case 'processing':
        return <Loader className="w-12 h-12 animate-spin text-green-600" />;
      case 'success':
        return <CheckCircle className="w-12 h-12 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-12 h-12 text-red-600" />;
    }
  };

  const getStatusColor = () => {
    switch (state.status) {
      case 'processing':
        return 'text-gray-700';
      case 'success':
        return 'text-green-700';
      case 'error':
        return 'text-red-700';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
      <div className="text-center max-w-md mx-auto px-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {getIcon()}
          </div>

          {/* Status Message */}
          <h2 className={`text-xl font-semibold mb-4 ${getStatusColor()}`}>
            {state.message}
          </h2>

          {/* Processing Details */}
          {state.status === 'processing' && (
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Securing your session...</span>
              </div>
            </div>
          )}

          {/* Success Details */}
          {state.status === 'success' && (
            <div className="text-sm text-gray-600">
              <p>Redirecting you to Coach Cedar...</p>
            </div>
          )}

          {/* Error Details */}
          {state.status === 'error' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                We encountered an issue during authentication.
              </p>
              {state.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-700 font-mono">
                    {state.error}
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Redirecting to login page in a few seconds...
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
